import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import MaterialTextField from '@mui/material/TextField';
import MaterialCheckbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from 'tss-react/mui';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import ConfirmButton from '../confirm-button';
import { requestJoinEventMutation } from '../../apollo/events';
import { useAppContext } from '../../contexts/app';
import Markdown from '../markdown';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    display: 'block',
    margin: theme.spacing(1)
  },
  helperText: {
    paddingLeft: theme.spacing(4),
    marginTop: 0
  },
  prompt: {
    '& > *:first-of-type': {
      marginTop: 0
    }
  },
  joinMessage: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  joinIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

type Props = {
  event?: Event;
  returnTo?: string;
  onJoin?: (...args: unknown[]) => void;
};

const RequestJoinButton = ({ event, returnTo, onJoin }: Props) => {
  const { isLoggedIn, loggedInUser, login } = useAppContext();
  const [requestJoinEvent] = useMutation(requestJoinEventMutation);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [membership, setMembership] = useState({
    phoneNumberVisibility: 'NONE',
    emailVisibility: 'NONE'
  });
  const { classes } = useStyles();

  if (event.membership) {
    if (event.membership.status === 'DENIED') {
      return (
        <p><strong>The host denied your join request.</strong></p>
      );
    }
    if (event.membership.status === 'REQUESTED') {
      return (
        <div className={classes.joinMessage}>
          <PauseCircleFilled className={classes.joinIcon} />
          <strong>Approval pending. You will be notified when the host responds.</strong>
        </div>
      );
    }
  }

  if (!isLoggedIn) {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={(e) => {
          e.preventDefault();
          login(returnTo);
        }}
      >
        Request to Join
      </Button>
    );
  }

  return (
    <ConfirmButton
      label="Request to Join"
      message="Request to Join"
      variant="contained"
      color="primary"
      onClick={() => {
        setSubmitting(true);
        requestJoinEvent({
          variables: {
            id: event.id,
            message,
            membership
          }
        }).then(() => {
          setSubmitting(false);
          onJoin();
        }).catch(() => {
          alert('An error occurred while requesting. Please try again.');
          setSubmitting(false);
        });
      }}
      disabled={submitting}
    >
      <DialogContentText component="div" className={classes.prompt}>
        <Markdown>{event.approvalPrompt || 'Include a message to the event host.'}</Markdown>
      </DialogContentText>
      <MaterialTextField
        autoFocus
        id="message"
        label="Message to Host"
        fullWidth
        multiline
        rows={3}
        maxRows={20}
        variant="outlined"
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
        }}
        disabled={submitting}
      />
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="emailVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  emailVisibility: e.target.checked ? 'HOST' : 'NONE'
                });
              }}
              value={true}
              checked={membership.emailVisibility === 'HOST'}
            />
          )}
          label="Share email with host"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.email}
        </FormHelperText>
      </FormControl>
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="phoneNumberVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  phoneNumberVisibility: e.target.checked ? 'HOST' : 'NONE'
                });
              }}
              value={true}
              checked={membership.phoneNumberVisibility === 'HOST'}
            />
          )}
          label="Share phone number with host"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.phoneNumberDisplay || 'None set (add in account preferences)'}
        </FormHelperText>
      </FormControl>
    </ConfirmButton>
  );
};

export default RequestJoinButton;
